import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from 'types';
import { PriceType } from 'types/api/pricing.type';

import { priceTypeStore } from 'lib/priceTypeStore';

import { SingleProductContext } from 'contexts';

import { usePriceVisible } from 'hooks/usePriceVisible';
import { useScaleColumns } from 'hooks/useScaleColumns';

import { parsePrice } from 'helpers/parsePrice';
import { getResolvedPriceByType } from 'helpers/pricing';

import { Select } from 'components/Select';
import { Table } from 'components/Table';

import { useScalesOrder } from './Scales.hooks';
import * as S from './Scales.styles';

const PRICE_TYPE_LABEL_KEYS: Record<PriceType, string> = {
  DISTRIBUTOR: 'catalogue.distributorPrice',
  WHOLESALE: 'catalogue.wholesalePrice',
  RETAIL: 'catalogue.retailPrice',
};

export function Scales(): React.ReactElement {
  const {
    productOrder,
    product,
    selectedPriceType,
    selectedPriceTypeSet,
    availablePriceTypes,
  } = useContext(SingleProductContext);
  const { t } = useTranslation();
  const viewPrice = usePriceVisible();
  const { isInOrder, control, savingInputName, onQuantityBlur } = useScalesOrder();

  const columns = useScaleColumns({
    scale: product?.scale,
    colorCode: product?.colorCode,
    quantities: productOrder?.quantities,
    isAvailable: productOrder?.available,
    isInOrder,
    control,
    savingInputName,
    onQuantityBlur,
  });

  const orderSizes = productOrder?.sizes ?? [];

  const scaleDataMapped = product?.sizeQtyPrice.map((item, i) => {
    const orderSize = orderSizes.find((s) => s.size === item.size);
    const orderQuantity = orderSize?.quantity ?? 0;

    // Use resolvedPrices exclusively — no legacy price fallback
    const resolved = getResolvedPriceByType(item.resolvedPrices, selectedPriceType);

    let priceValue: string;
    let currency: string;

    if (resolved && resolved.status === 'FOUND' && resolved.price) {
      priceValue = resolved.price.price;
      currency = resolved.price.currency;
    } else {
      priceValue = '';
      currency = '';
    }

    const priceNum = parsePrice(priceValue || '0');

    return {
      size: item.size,
      minQty: item.minQty,
      price: priceValue,
      currency,
      quantity: product.sizeQtyPrice[i].quantity ?? 0,
      orderQuantity,
      rowTotal: (priceNum * orderQuantity).toFixed(2),
    };
  });

  const priceTypeOptions: Option[] = useMemo(
    () =>
      availablePriceTypes.map((pt) => ({
        value: pt,
        label: t(PRICE_TYPE_LABEL_KEYS[pt] as any), // eslint-disable-line
      })),
    [availablePriceTypes, t]
  );

  const selectedOption = useMemo(
    () => priceTypeOptions.find((o) => o.value === selectedPriceType),
    [priceTypeOptions, selectedPriceType]
  );

  const handlePriceTypeChange = (option: Option) => {
    selectedPriceTypeSet(option.value as PriceType);
    priceTypeStore.setPriceType(option.value as PriceType);
  };

  const showPriceSelector = viewPrice && availablePriceTypes.length > 1;

  return (
    <S.Container>
      <S.Header>
        <S.Title tag="h4" scale="lg">
          {t('catalogue.sizes')}
        </S.Title>

        {showPriceSelector ? (
          <S.PriceTypeSelector>
            <Select
              name="priceType"
              label={String(t('catalogue.priceType'))}
              options={priceTypeOptions}
              value={selectedOption}
              onChange={handlePriceTypeChange}
              motive="default"
            />
          </S.PriceTypeSelector>
        ) : null}
      </S.Header>

      {scaleDataMapped ? (
        <Table motive="tertiary" columns={columns} data={scaleDataMapped} />
      ) : undefined}
    </S.Container>
  );
}
